import { CircularProgress, Stack } from '@mui/joy'
import { useGate, useStore } from 'effector-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { appealModel } from '~/entities/common/Balance/Appeal'
import {
  ViewAppeal,
  Status,
  ActionButtons,
} from '~/features/common/Balance/Appeal/ViewAppeal'
import { formatDateTimeForUI } from '~/shared/lib/date'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { Container, PageContainer, PageTitle } from '~/shared/ui/Layout'
import { NotFound } from '~/shared/ui/NotFound'
import {
  QuickNavigation,
  QuickNavigationOptions,
} from '~/shared/ui/QuickNavigation'

const quickNavigationOptions: QuickNavigationOptions = [
  { labelI18n: 'basic info', anchor: 'main' },
  { labelI18n: 'resolution', anchor: 'solution' },
  { labelI18n: 'files', anchor: 'documents' },
]

export function ViewAppealPage() {
  const { t } = useTranslation()
  const { appealId } = useParams<{ appealId: string }>()

  useGate(appealModel.Gate, { appealId })

  const isLoading = useStore(appealModel.requestFx.pending)
  const appeal = useStore(appealModel.$appeal)
  const carError = appealModel.useAppealError(appealId)

  const title = useMemo(() => {
    if (!appeal) return ''

    return t('number and date', {
      number: appeal?.getNumber(),
      createAt: formatDateTimeForUI(appeal?.getCreatedAt() as string),
    })
  }, [appeal, t])

  const breadcrumbs: BreadcrumbsList = useMemo(
    () => [
      { titleI18n: 'balances', link: '/balance/balance-histories' },
      { titleI18n: 'requests', link: '/balance/appeals' },
      { title },
    ],
    [title],
  )

  if (!appealId || carError) {
    return <NotFound message={t('alert.failed to load request')} />
  }

  return (
    <PageContainer title={title}>
      <Container isHeader>
        <Breadcrumbs list={breadcrumbs} lastLoading={isLoading} />
        <Status />
        <Stack direction='row' alignItems='end' justifyContent='space-between'>
          <PageTitle loading={isLoading}>{title}</PageTitle>
          {isLoading && <CircularProgress />}
          {!isLoading && <ActionButtons appealId={appealId} />}
        </Stack>
      </Container>

      <ViewAppeal />

      <QuickNavigation options={quickNavigationOptions} />
    </PageContainer>
  )
}
